import { useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { CardHead } from '../../design-system/composites/Card/Card';
import { CheckIcon } from '@phosphor-icons/react/dist/csr/Check';
import { ShieldCheckIcon } from '@phosphor-icons/react/dist/csr/ShieldCheck';
import { ToggleLeftIcon } from '@phosphor-icons/react/dist/csr/ToggleLeft';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { QrCode } from './QrCode';
import { RecoveryCodeList } from './RecoveryCodeList';
import { Button } from '../../design-system/primitives/Button/Button';
import { Input } from '../../design-system/primitives/Input/Input';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
  VALIDATION_FAILED_CODE,
} from '../../lib/api/client';
import { CODE_LENGTH, digitsOnly } from '../auth/otpCode';
import type { MfaEnrolment, RecoveryCodes } from '../../lib/api/mfa';
import styles from './MfaEnrolmentScreen.module.css';

/**
 * Turning on two-step verification, in the three states the API's two-call
 * design requires.
 *
 *     idle  --begin()-->  transferring  --confirm(code)-->  codes
 *
 * ## Why it is three states and not one screen
 *
 * `POST /profile/mfa` mints a secret and stores it UNCONFIRMED; the account
 * does not hold a second factor until `POST /profile/mfa/confirm` succeeds.
 * That is not ceremony — it is what makes abandoning safe. If the transfer
 * failed (a mistyped key, a QR that never scanned, a phone that was already
 * wiped) the user simply leaves, and their next sign-in is unaffected, because
 * an unconfirmed method is not a factor. Collapsing the two calls into one
 * would produce accounts holding a confirmed factor nobody can generate codes
 * for, and the only way back would be an administrator.
 *
 * ## The secret leaves the server exactly once
 *
 * `data.secret` in the `201` is the only time it is readable, and it is held in
 * React state for the life of this component and nowhere else. It is not
 * logged, not persisted, and not put in the URL. Once confirmed it cannot be
 * read back at all — a user who loses it re-enrols.
 *
 * ## Idempotent by design
 *
 * Calling `begin()` again while an enrolment is unconfirmed REPLACES it with a
 * fresh secret, so a user who reloads mid-transfer is not stuck against a
 * `UNIQUE(user_id, type)` collision. Calling it once a factor is confirmed
 * answers `409`, which is rendered as its own message rather than as a
 * credential failure: the request was fine and the state forbids it.
 *
 * ## Managing a factor that already exists
 *
 * Two more states, added after M-01a shipped the endpoints for them with
 * nothing in the interface able to call either:
 *
 *     idle  --> disabling     --(password + code)-->  idle, no factor
 *     idle  --> regenerating  --(password)--------->  codes
 *
 * **Regeneration is the lost-phone path, and it is the reason it costs no
 * code.** Somebody who signed in with a recovery code cannot produce a TOTP
 * code, so every door that asks for one is shut to them: `begin()` answers 409
 * while a factor is confirmed, and disabling needs a code by design. Without
 * this control they spend down nine remaining codes and then need an
 * administrator. The cost is stated in the API's own docblock and does not
 * change here: a session plus a password mints ten durable credentials.
 */

export interface MfaEnrolmentScreenProps {
  /** True when the account already holds a confirmed factor. */
  enabled: boolean;
  onBegin: () => Promise<MfaEnrolment>;
  onConfirm: (code: string) => Promise<RecoveryCodes>;
  /** Called after the user acknowledges the recovery codes of a NEW enrolment. */
  onEnrolled?: () => void;
  onDisable: (password: string, code: string) => Promise<unknown>;
  onRegenerate: (password: string) => Promise<RecoveryCodes>;
  /** Called once the factor is gone. */
  onDisabled?: () => void;
}

type Phase =
  | { name: 'idle' }
  | { name: 'transferring'; enrolment: MfaEnrolment }
  | { name: 'disabling' }
  | { name: 'regenerating' }
  /**
   * `enrolled` records WHY the codes are on screen. Enrolment and regeneration
   * both end here and both show the same list, but only one of them is news to
   * the parent — firing `onEnrolled` after a regeneration would make "a factor
   * now exists" and "the same factor got new codes" indistinguishable to
   * anything counting either.
   */
  | { name: 'codes'; codes: readonly string[]; enrolled: boolean };

export function MfaEnrolmentScreen({
  enabled,
  onBegin,
  onConfirm,
  onEnrolled,
  onDisable,
  onRegenerate,
  onDisabled,
}: MfaEnrolmentScreenProps) {
  const { t } = useTranslation();

  const [phase, setPhase] = useState<Phase>({ name: 'idle' });
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [reference, setReference] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /** Read inside the handlers: two clicks in one React batch both see `false`. */
  const inFlight = useRef(false);

  /**
   * `conflictMessage` is a PARAMETER and not `t('mfa.alreadyOn')`, because the
   * two endpoints that answer 409 mean opposite things by it. On `begin()` it
   * is "you already have a factor"; on `destroy()` it is "your account requires
   * one and you may not remove it". A single message would tell a user who just
   * asked to turn the factor off that it is already on — answering a question
   * they did not ask, and hiding the only fact that matters, which is that an
   * administrator has to act.
   */
  function describe(error: ApiError, credentialMessage: string, conflictMessage: string): string {
    // 409 first: it is the one failure here that is about STATE rather than
    // about what the user typed, and describing it as a bad code would send
    // them off to check their phone for a problem that is not there.
    if (error.status === 409) return conflictMessage;

    switch (error.code) {
      case VALIDATION_FAILED_CODE:
        return credentialMessage;
      case TOO_MANY_REQUESTS_CODE:
        return t('auth.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('auth.unexpected');
    }
  }

  function fail(
    error: unknown,
    credentialMessage: string,
    conflictMessage: string = t('mfa.alreadyOn'),
  ): void {
    setNotice(null);

    if (!(error instanceof ApiError)) {
      // A fault in our own code. The agent must be told the control did
      // nothing, and the fault must not be disguised as a credential problem.
      setFailure(t('auth.unexpected'));
      setReference(null);
      throw error;
    }

    setFailure(describe(error, credentialMessage, conflictMessage));
    setReference(error.requestId ?? null);
  }

  /**
   * Leave whichever form is open, taking the credentials in it with us.
   *
   * THE ONLY PLACE A CREDENTIAL IS CLEARED, and that is deliberate. An earlier
   * version also cleared on open, which sounds harmless and is not: it made a
   * password left behind by a completed action unobservable, because the next
   * form to open wiped it on the way in. A mutation run is what showed that —
   * the "password survives a successful disable" mutant survived against a test
   * that could not have failed. One owner means the clearing can be seen.
   */
  function closeForm(): void {
    setPhase({ name: 'idle' });
    setCode('');
    setPassword('');
    setFailure(null);
    setReference(null);
  }

  /**
   * Open a management form. Clears the ALERT only — never a credential.
   *
   * DO NOT ADD `setPassword('')` HERE, however tidy it looks. It changes no
   * behaviour — `closeForm()` has already cleared on every path that reaches
   * idle — and it costs the suite its only view of that: with the fields wiped
   * on the way in, a success path that forgot to clear becomes invisible, and
   * the two mutants covering exactly that would survive against tests which
   * could not have failed. That is not a hypothesis; it is what the first
   * version of this component did.
   *
   * It takes the phase object rather than a name string because `setPhase`
   * cannot narrow `{ name: 'disabling' | 'regenerating' }` back into the
   * discriminated union — the compiler is right to refuse it, and the literal
   * at each call site is what it wants.
   */
  function openForm(next: { name: 'disabling' } | { name: 'regenerating' }): void {
    setFailure(null);
    setReference(null);
    setPhase(next);
  }

  async function begin(): Promise<void> {
    if (inFlight.current) return;

    inFlight.current = true;
    setBusy(true);
    setFailure(null);
    setReference(null);
    setNotice(t('mfa.starting'));

    try {
      const enrolment = await onBegin();
      setNotice(null);
      setCode('');
      setPhase({ name: 'transferring', enrolment });
    } catch (error) {
      fail(error, t('auth.unexpected'));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  async function confirm(event: FormEvent<HTMLFormElement>): Promise<void> {
    // FIRST statement: everything below can throw, and a default-action submit
    // on a form with no method is a GET that would put the code in the URL.
    event.preventDefault();

    if (inFlight.current || code.length !== CODE_LENGTH) return;

    inFlight.current = true;
    setBusy(true);
    setFailure(null);
    setReference(null);

    try {
      const { recovery_codes: codes } = await onConfirm(code);
      setCode('');
      setPhase({ name: 'codes', codes, enrolled: true });
    } catch (error) {
      fail(error, t('mfa.confirmRejected'));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  async function disable(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();

    if (inFlight.current || password === '' || code.length !== CODE_LENGTH) return;

    inFlight.current = true;
    setBusy(true);
    setFailure(null);
    setReference(null);

    try {
      await onDisable(password, code);
      // Success clears the credentials with the form. On FAILURE they stay:
      // one of the two was wrong and the user has no way to know which, so
      // making them retype both would be punishing them for our own refusal to
      // say which it was.
      closeForm();
      setNotice(t('mfa.disabledNotice'));
      onDisabled?.();
    } catch (error) {
      fail(error, t('mfa.disableRejected'), t('mfa.disableRequired'));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  async function regenerate(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();

    if (inFlight.current || password === '') return;

    inFlight.current = true;
    setBusy(true);
    setFailure(null);
    setReference(null);

    try {
      const { recovery_codes: codes } = await onRegenerate(password);
      setPassword('');
      setPhase({ name: 'codes', codes, enrolled: false });
    } catch (error) {
      // No 409 is specified for this endpoint, so an unexpected one is exactly
      // that rather than a state the user can act on.
      fail(error, t('mfa.regenerateRejected'), t('auth.unexpected'));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  /** The password field, identical in both management forms. */
  function passwordInput() {
    return (
      <Input
        label={t('mfa.passwordLabel')}
        hint={t('mfa.passwordHint')}
        width="fill"
        type="password"
        // `current-password`, so the manager the agent actually uses offers the
        // right entry. `new-password` here would prompt it to invent one.
        autoComplete="current-password"
        required
        value={password}
        onChange={(event) => {
          setPassword(event.target.value);
        }}
      />
    );
  }

  return (
    <section className={styles['panel']} aria-labelledby="mfaSectionTitle" data-testid="mfaPanel">
      <CardHead
        id="mfaSectionTitle"
        level="h3"
        size="md"
        icon={<ShieldCheckIcon size={20} weight="duotone" />}
        title={t('mfa.sectionTitle')}
      />

      <p className={styles['status']} data-testid="mfaStatus">
        {enabled ? t('mfa.statusOn') : t('mfa.statusOff')}
      </p>

      {/*
        Both live regions mounted from the first frame, EMPTY — assistive
        technology announces changes inside a region it is already watching.
      */}
      <div className={styles['alert']} role="alert" aria-atomic="true" data-testid="mfaAlert">
        {failure === null ? null : <span className={styles['alertText']}>{failure}</span>}
        {reference === null ? null : (
          <span className={styles['reference']}>
            {t('auth.supportReference')}{' '}
            <bdi dir="ltr" data-testid="mfaRequestId">
              {reference}
            </bdi>
          </span>
        )}
      </div>

      <div className={styles['notice']} role="status" aria-atomic="true" data-testid="mfaNotice">
        {notice}
      </div>

      {phase.name === 'idle' && !enabled ? (
        <Button
          icon={<ShieldCheckIcon size={16} />}
          loading={busy}
          onClick={() => {
            void begin();
          }}
        >
          {t('mfa.start')}
        </Button>
      ) : null}

      {/*
        The two management controls. Opening either CLOSES the other — two
        password fields on one panel is two places to type a password into, and
        only one of them does what the heading above it says.
      */}
      {phase.name === 'idle' && enabled ? (
        <div className={styles['actions']}>
          <Button
            icon={<ShieldCheckIcon size={16} />}
            variant="secondary"
            onClick={() => {
              openForm({ name: 'regenerating' });
            }}
          >
            {t('mfa.regenerate')}
          </Button>

          <Button
            icon={<ToggleLeftIcon size={16} />}
            variant="ghost"
            onClick={() => {
              openForm({ name: 'disabling' });
            }}
          >
            {t('mfa.disable')}
          </Button>
        </div>
      ) : null}

      {phase.name === 'disabling' ? (
        <form
          className={styles['transfer']}
          method="post"
          noValidate
          data-testid="mfaDisableForm"
          onSubmit={(e) => void disable(e)}
        >
          <h4 className={styles['step']}>{t('mfa.disable')}</h4>
          {/*
            What it destroys, BEFORE the credentials rather than after them.
            The recovery codes go with the factor, server-side, and a user who
            believed their printed sheet still worked would find that out at
            their next sign-in.
          */}
          <p className={styles['stepIntro']}>{t('mfa.disableWarning')}</p>

          {passwordInput()}

          <Input
            label={t('mfa.confirmLabel')}
            hint={t('mfa.confirmHint')}
            width="fill"
            numeric
            autoComplete="one-time-code"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            maxLength={CODE_LENGTH}
            required
            value={code}
            onChange={(event) => {
              setCode(digitsOnly(event.target.value));
            }}
          />

          <div className={styles['actions']}>
            <Button
              icon={<ToggleLeftIcon size={16} />}
              type="submit"
              variant="destructive"
              loading={busy}
              disabled={password === '' || code.length !== CODE_LENGTH}
            >
              {t('mfa.disableSubmit')}
            </Button>

            <Button icon={<XIcon size={16} />} type="button" variant="ghost" onClick={closeForm}>
              {t('mfa.cancel')}
            </Button>
          </div>
        </form>
      ) : null}

      {phase.name === 'regenerating' ? (
        <form
          className={styles['transfer']}
          method="post"
          noValidate
          data-testid="mfaRegenerateForm"
          onSubmit={(e) => void regenerate(e)}
        >
          <h4 className={styles['step']}>{t('mfa.regenerate')}</h4>
          <p className={styles['stepIntro']}>{t('mfa.regenerateWarning')}</p>

          {/*
            NO CODE FIELD, and that is the whole point of this control. A user
            who lost their authenticator cannot produce one, and they are who
            this endpoint exists for.
          */}
          {passwordInput()}

          <div className={styles['actions']}>
            <Button icon={<ShieldCheckIcon size={16} />} type="submit" loading={busy} disabled={password === ''}>
              {t('mfa.regenerateSubmit')}
            </Button>

            <Button icon={<XIcon size={16} />} type="button" variant="ghost" onClick={closeForm}>
              {t('mfa.cancel')}
            </Button>
          </div>
        </form>
      ) : null}

      {phase.name === 'transferring' ? (
        <form className={styles['transfer']} method="post" noValidate onSubmit={(e) => void confirm(e)}>
          <h4 className={styles['step']}>{t('mfa.scanTitle')}</h4>
          <p className={styles['stepIntro']}>{t('mfa.scanIntro')}</p>

          <QrCode value={phase.enrolment.otpauth_uri} label={t('mfa.qrAlt')} />

          {/*
            The secret in text, always — not behind a "can't scan?" toggle. A
            screen-reader user cannot scan anything, and a desktop-only agent
            with the authenticator on a locked-down phone often cannot either.
            `<bdi dir="ltr">` because it is a code identifier, and a `<code>`
            element because that is what it is.
          */}
          <label className={styles['manualLabel']} htmlFor="mfaSecret">
            {t('mfa.manualLabel')}
          </label>
          <code className={styles['secret']} id="mfaSecret" data-testid="mfaSecret" tabIndex={-1}>
            <bdi dir="ltr">{phase.enrolment.secret}</bdi>
          </code>

          <Input
            label={t('mfa.confirmLabel')}
            hint={t('mfa.confirmHint')}
            width="fill"
            numeric
            autoComplete="one-time-code"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            maxLength={CODE_LENGTH}
            required
            value={code}
            onChange={(event) => {
              setCode(digitsOnly(event.target.value));
            }}
          />

          <div className={styles['actions']}>
            <Button icon={<CheckIcon size={16} />} type="submit" loading={busy} disabled={code.length !== CODE_LENGTH}>
              {t('mfa.confirm')}
            </Button>

            <Button
              icon={<XIcon size={16} />}
              type="button"
              variant="ghost"
              onClick={() => {
                // Local only. The unconfirmed row stays on the server, where it
                // is harmless — it is not a factor until it is confirmed — and
                // the next begin() replaces it.
                setPhase({ name: 'idle' });
                setCode('');
                setFailure(null);
                setReference(null);
              }}
            >
              {t('mfa.cancel')}
            </Button>
          </div>
        </form>
      ) : null}

      {phase.name === 'codes' ? (
        <RecoveryCodeList
          codes={phase.codes}
          onAcknowledge={
            phase.enrolled
              ? () => {
                  setPhase({ name: 'idle' });
                  onEnrolled?.();
                }
              : () => {
                  setPhase({ name: 'idle' });
                }
          }
        />
      ) : null}
    </section>
  );
}
